import React, { useState, useEffect } from 'react';
import { IProps, IDonationWidgetProps } from './DonationBlock.interface';
import { clsx } from 'clsx';
import Icon from '@/elements/Icon/Icon.component';
import { RadioButtons } from '@/elements/RadioButtons/RadioButtons.component';
import PayPalDonateButton from './PayPalDonateButton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/elements/Tabs/Tabs.component';
import StripeCheckout from './StripeCheckout';
import DonationButtons from './DonationButtons';
import { IconNames } from '@/elements/Icon/Icon.interface';
import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, {
  ContentColumn,
} from '@/layouts/ContentWrapper/ContentWrapper.component';
import './DonationBlock.css'

export const DonationWidget = (props: IDonationWidgetProps) => {
  const {
    formId,
    alternativePaymentLabel,
    alternativePaymentLink,
    amountLabel,
    oneoffAmounts,
    monthlyAmounts,
    footerIcons = ['cc-visa', 'cc-mastercard', 'cc-paypal', 'cc-amex'],
    payPalPaymentSettings = {
      clientId: undefined,
      orderApproveApiUrl: undefined,
      orderCreateApiUrl: undefined,
      scriptUrl: undefined
    },
    creditCardOptionLabel,
    payPalOptionLabel,
    oneOffTabLabel,
    monthlyTabLabel,
    currencySymbol,
    currencyCode,
    otherAmountLabel,
    stripePaymentSettings,
    donateButtonLabel,
    minimumOneOffAmount,
    maximumOneOffAmount,
    minimumMonthlyAmount,
    maximumMonthlyAmount,
    isMegaMenu = false,
    formName = 'default'
  } = props;

  const radioButtonsOptions = [
    { label: creditCardOptionLabel, value: 'card' },
    { label: payPalOptionLabel, value: 'paypal' },
  ];

  const { clientId, orderApproveApiUrl, orderCreateApiUrl, scriptUrl } =
    payPalPaymentSettings;
  const [selectedValue, setSelectedValue] = useState<string>('card');
  const [paymentMethod, setPaymentMethod] = useState<'credit' | 'paypal'>(
    'credit',
  );
  const [activeTab, setActiveTab] = useState('once');
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);

  useEffect(() => {
    setSelectedAmount(null);
  }, [activeTab]);

  const donationTabs = [
    {
      trigger: 'Once',
      label: oneOffTabLabel,
      content: {
        name: `DonationButtons_${formName}`,
        props: {
          heading: amountLabel,
          options: oneoffAmounts?.map((amount) => ({
            ...amount,
            type: 'one-off',
          })),
          currency: currencyCode,
          customAmountLabel: otherAmountLabel,
          onAmountSelection: setSelectedAmount,
        },
      },
    },
    {
      trigger: 'Monthly',
      label: monthlyTabLabel,
      content: {
        name: `DonationButtons_${formName}`,
        props: {
          heading: amountLabel,
          options: monthlyAmounts?.map((amount) => ({
            ...amount,
            type: 'monthly',
          })),
          currency: currencyCode,
          customAmountLabel: otherAmountLabel,
          onAmountSelection: setSelectedAmount,
        },
      },
    },
  ];

  const handleRadioButtons = (value: string) => {
    setSelectedValue(value);
    setPaymentMethod(value === 'paypal' ? 'paypal' : 'credit');
  };
  return (
    <div className='donationWidget__container'>
      <section className="tabContainerBlock" data-blk="TabContainerBlock">

        <Tabs
          defaultValue="once"
          className="w-full"
          onValueChange={setActiveTab}
        >
          <div className="mx-auto w-full max-w-[1600px] bg-white">
            <TabsList className="container mx-auto overflow-auto md:overflow-visible">
              {donationTabs?.map((tab) => (
                <TabsTrigger
                  key={tab.trigger}
                  className="w-full px-4 md:px-6"
                  value={tab.trigger.toLowerCase()}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {donationTabs?.map((tab, tabIndex) => (
            <TabsContent key={tabIndex} value={tab.trigger.toLowerCase()}>
              <DonationButtons
                currencySymbol={currencySymbol}
                heading={tab.content.props.heading}
                options={tab.content.props.options}
                onAmountSelection={tab.content.props.onAmountSelection}
                minimumOneOffAmount={minimumOneOffAmount}
                maximumOneOffAmount={maximumOneOffAmount}
                minimumMonthlyAmount={minimumMonthlyAmount}
                maximumMonthlyAmount={maximumMonthlyAmount}
                isMegaMenu={isMegaMenu}
                id={`${tab.content.name}_${formName}`}
                otherAmountLabel={otherAmountLabel}
              />
            </TabsContent>
          ))}
        </Tabs>

      </section>

      <div className={clsx('flex flex-col p-4')}>
        <div className="mb-6">
          <RadioButtons
            name={`PaymentMethod_${formName}`}
            options={radioButtonsOptions}
            selectedValue={selectedValue}
            onChange={handleRadioButtons}
            inputType='radio'
          />
        </div>


        {alternativePaymentLabel && alternativePaymentLink && (
          <a
            className="my-5 text-base no-underline text-copy"
            href={alternativePaymentLink}
            target="_blank"
            aria-label="link to alternative payments opens a new tab"
            rel="noopener noreferrer"
          >
            {alternativePaymentLabel}
            <Icon icon="external-link" className="ml-2" />
          </a>
        )}
        {paymentMethod === 'credit' ? (
          <StripeCheckout
            selectedAmount={selectedAmount}
            isMonthly={activeTab === 'monthly'}
            formId={formId}
            donateButtonLabel={donateButtonLabel}
            currencyCode={currencyCode}
            {...stripePaymentSettings}
          />
        ) : paymentMethod === 'paypal' && clientId ? (
          <PayPalDonateButton
            clientId={clientId}
            createOrderApiUrl={orderCreateApiUrl}
            onApproveApiUrl={orderApproveApiUrl}
            selectedAmount={selectedAmount || 0}
            isRecurring={activeTab === 'monthly'}
            isDisabled={!selectedAmount}
            formId={formId}
            currencyCode={currencyCode}
          />
        ) : null}
        {footerIcons && footerIcons?.length > 0 && (
          <div className="mx-auto mt-5 flex gap-4">
            {footerIcons?.map((footerItem) => (
              <Icon key={footerItem} icon={footerItem as IconNames} className='donationBlock__footer--icon' />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const DonationBlock = (props: IProps) => {

  const { donateform, ...rest } = props;


  return (
    <BlockWrapper className="donationBlock" {...rest}>
      <ContentWrapper>
        <ContentColumn>
          <DonationWidget {...donateform} key={`donation block`} />
        </ContentColumn>
      </ContentWrapper>
    </BlockWrapper>
  );
};

export default DonationBlock;
